/* global bootstrap */
import { computed } from 'vue'
import axios from 'axios'
import BpmOsgpoModule from '@/helpers/Modules/Product/BpmOsgpoModule'
import { normalizeIdentity } from '@/helpers/Modules/Product/bpm/processIdentity'
import {
  getProcessIin,
  getProcessNomerVu,
  processMatchesClient,
  processMatchesVehicle,
} from '@/helpers/Modules/Product/bpm/processMatch'

export function useBpmPolling ({
  policy,
  mode,
  notify,
  router,
  sharedApi,
  interval,
  processCheckInFlight,
  isBpmModalOpened,
  bpmModalClosable,
  changeDisabled,
  globalLoader,
  isSendPayment,
  processModalState,
}) {
  function findInsurerProcessForInsured (process) {
    const iin = getProcessIin(process)
    if (!iin || !Array.isArray(policy.business_process)) {
      return null
    }
    return policy.business_process.find(candidate =>
      candidate?.type === 1 && getProcessIin(candidate) === iin,
    ) ?? null
  }

  function shouldRunSeparateInsuredBpForInsurer (process) {
    if (process?.type !== 2) {
      return false
    }
    if (policy?.insurer && sharedApi.isEntrepreneurClient(policy.insurer)) {
      return false
    }
    const insuredVu = getProcessNomerVu(process)
    if (!insuredVu) {
      return false
    }
    const insurerProcess = findInsurerProcessForInsured(process)
    const hasInsurerEntityVu = !!normalizeIdentity(policy?.insurer?.licence?.number)
    if (!insurerProcess || BpmOsgpoModule.BusinessProcess.isCompleted(insurerProcess)) {
      return !hasInsurerEntityVu
    }
    return !getProcessNomerVu(insurerProcess)
  }

  function prepareSeparateInsuredBp (process, index) {
    policy.business_process[index].mirror_of = null
    const iin = getProcessIin(process)
    if (!iin || !Array.isArray(policy.business_process)) {
      return
    }
    const keepId = process.id
    policy.business_process = policy.business_process.filter(candidate =>
      candidate.id === keepId ||
      candidate.type !== 2 ||
      !candidate.mirror_of ||
      getProcessIin(candidate) !== iin ||
      !!getProcessNomerVu(candidate),
    )
  }

  function canPollProcess (process) {
    if (!process) {
      return false
    }
    const processData = process.process_data
    if (!processData || !processData.status) {
      return false
    }
    if (['FAILED', 'TIME_OUT', 'TIMED_OUT', 'COMPLETED', 'WAITING_USER'].includes(String(processData.status))) {
      return false
    }
    return !!getProcessCheckId(process)
  }

  function isProcessRejected (processData) {
    return !!(processData && ['FAILED', 'TIME_OUT', 'TIMED_OUT'].includes(String(processData.status)))
  }

  function isInsurerMatchesInsuredProcess (process) {
    if (!process || !policy?.insurer) {
      return false
    }
    return processMatchesClient(process, policy.insurer)
  }

  function findStartedProcessWithSameIin (process) {
    const iin = getProcessIin(process)
    if (!iin) {
      return null
    }
    if (!Array.isArray(policy.business_process)) {
      return null
    }
    if (shouldRunSeparateInsuredBpForInsurer(process)) {
      return null
    }
    return policy.business_process.find(candidate => {
      if (!candidate || candidate.id === process.id) return false
      if (candidate.mirror_of) return false
      if (!BpmOsgpoModule.BusinessProcess.isStarted(candidate)) return false
      return getProcessIin(candidate) === iin
    }) || null
  }

  function startInterval () {
    interval.value = setInterval(() => {
      handleBusinessProcess()
    }, 1000)
  }

  function stopInterval () {
    clearInterval(interval.value)
  }

  function addProcess (type, init_data, meta = null, status = 1) {
    const process = BpmOsgpoModule.BusinessProcess.create({ withProcessData: type !== 5 })
    process.type = type
    process.status = status
    process.init_data = init_data
    process.meta = meta
    BpmOsgpoModule.BusinessProcess.touch(process)
    policy.business_process.push(process)
    return process
  }

  function addMirrorProcess (sourceProcess, type, init_data, meta = null, status = 2) {
    if (!sourceProcess || !sourceProcess.id || !Array.isArray(policy.business_process)) {
      return null
    }
    const searchIin = normalizeIdentity(meta?.search?.iin ?? init_data?.iin)
    const searchDocument = normalizeIdentity(meta?.search?.nomer_vu ?? init_data?.nomer_vu)
    const hasSameProcess = policy.business_process.some(process => {
      if (!process || process.id === sourceProcess.id || process.type !== type) {
        return false
      }
      if (BpmOsgpoModule.BusinessProcess.isCompleted(process)) {
        return false
      }
      const processIin = normalizeIdentity(process?.meta?.search?.iin ?? process?.init_data?.iin)
      const processDocument = normalizeIdentity(process?.meta?.search?.nomer_vu ?? process?.init_data?.nomer_vu)
      return !!(
        (searchIin && processIin && searchIin === processIin) ||
        (searchDocument && processDocument && searchDocument === processDocument)
      )
    })
    if (hasSameProcess) {
      return null
    }
    const mirrorProcess = addProcess(type, init_data, meta, status)
    if (!mirrorProcess) {
      return null
    }
    mirrorProcess.mirror_of = sourceProcess.id
    mirrorProcess.process_data = structuredClone(sourceProcess.process_data)
    BpmOsgpoModule.BusinessProcess.touch(mirrorProcess)
    return mirrorProcess
  }

  function changeProcessStatus (id, status) {
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index !== -1) {
      const process = policy.business_process[index]
      if (
        process?.type === 2 &&
        policy?.insurer &&
        !sharedApi.isEntrepreneurClient(policy.insurer) &&
        isInsurerMatchesInsuredProcess(process) &&
        policy.insurer.licence?.number
      ) {
        policy.isDrive = true
        policy.business_process.splice(index, 1)
        return
      }
      if (status === 1) {
        const source = findStartedProcessWithSameIin(process)
        if (source) {
          policy.business_process[index].mirror_of = source.id
          policy.business_process[index].process_data = structuredClone(source.process_data)
          policy.business_process[index].status = 2
          BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
          if (source.process_data && source.process_data.status === 'COMPLETED') {
            completeProcess(id)
          }
          return
        }
        if (shouldRunSeparateInsuredBpForInsurer(process)) {
          prepareSeparateInsuredBp(process, index)
        }
      }
      policy.business_process[index].status = status
      BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
    }
  }

  function syncMirrors (sourceId) {
    if (!Array.isArray(policy.business_process)) {
      return
    }
    const sourceIndex = policy.business_process.findIndex(item => item.id === sourceId)
    if (sourceIndex === -1) {
      return
    }
    const sourceData = policy.business_process[sourceIndex].process_data
    policy.business_process.forEach((item, idx) => {
      if (!item || item.mirror_of !== sourceId) return
      if (BpmOsgpoModule.BusinessProcess.isCompleted(item)) return
      policy.business_process[idx].process_data = structuredClone(sourceData)
      BpmOsgpoModule.BusinessProcess.touch(policy.business_process[idx])
    })
    if (sourceData && sourceData.status === 'COMPLETED') {
      const mirrorIds = policy.business_process
        .filter(item => item?.mirror_of === sourceId && !BpmOsgpoModule.BusinessProcess.isCompleted(item))
        .map(item => item.id)
      mirrorIds.forEach(mirrorId => completeProcess(mirrorId))
    }
  }

  function deleteProcess (id) {
    delete processCheckInFlight.value[id]
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index !== -1) {
      policy.business_process.splice(index, 1)
    }
  }

  function removeBusinessProcessByEntity (entity) {
    if (!entity || !Array.isArray(policy.business_process)) {
      return
    }
    policy.business_process = policy.business_process.filter(
      item => {
        if (!item) return false
        if ([1, 2].includes(item.type)) {
          return !processMatchesClient(item, entity)
        }
        if (item.type === 3) {
          return !processMatchesVehicle(item, entity)
        }
        return true
      },
    )
  }

  function removeUnfinishedInsuredBusinessProcessByInsurer () {
    if (!Array.isArray(policy.business_process) || !policy.insurer) {
      return
    }

    policy.business_process = policy.business_process.filter(process => {
      if (process?.type !== 2 || BpmOsgpoModule.BusinessProcess.isCompleted(process)) {
        return true
      }
      return !processMatchesClient(process, policy.insurer)
    })
  }

  function restartProcess (id) {
    delete processCheckInFlight.value[id]
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index === -1) {
      return
    }
    policy.business_process[index].status = 1
    policy.business_process[index].process_data = BpmOsgpoModule.BusinessProcess.defaultProcessData()
    policy.business_process[index].pollErrors = 0
    policy.business_process[index].mirror_of = null
    BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
  }

  function getProcessUrl (type) {
    switch (type) {
      case 1:
      case 2:
        return '/api/v2/customer/bpm/search'
      case 3:
        return '/api/v2/vehicle/bpm/search'
      case 5:
        return mode.value.checkoutUrl
      default:
        return ''
    }
  }

  function getProcessCheckId (process) {
    return process?.async_process_id || process?.process_data?.id || null
  }

  function handleBusinessProcess () {
    for (const process of policy.business_process) {
      if (BpmOsgpoModule.BusinessProcess.isCompleted(process)) {
        continue
      }
      if (BpmOsgpoModule.BusinessProcess.isPendingConfirmation(process)) {
        continue
      }
      if (process.mirror_of) {
        continue
      }
      if (BpmOsgpoModule.BusinessProcess.isReady(process)) {
        startProcess(process.id, process.type)
        return
      }
      if (process.process_data && ['WAITING_USER'].includes(process.process_data.status)) {
        if (!isBpmModalOpened.value && process.type === 5) {
          openBpmModal(process.id, false)
          sharedApi.saveDraft()
        }
        continue
      }
      if (process.process_data && ['FAILED', 'TIME_OUT', 'TIMED_OUT'].includes(process.process_data.status)) {
        failedProcess(process.id)
        continue
      }
      if (process.process_data && ['COMPLETED'].includes(process.process_data.status)) {
        completeProcess(process.id)
        continue
      }
      if (BpmOsgpoModule.BusinessProcess.isStarted(process) && canPollProcess(process)) {
        checkProcess(process.id)
      }
    }
  }

  function startProcess (id, type) {
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index === -1) {
      return
    }
    if (!BpmOsgpoModule.BusinessProcess.isReady(policy.business_process[index])) {
      return
    }
    policy.business_process[index].status = 2
    BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
    const url = getProcessUrl(type)
    if (!url) {
      return
    }
    axios.post(url, policy.business_process[index].init_data)
      .then(res => {
        const processIndex = policy.business_process.findIndex(item => item.id === id)
        if (processIndex === -1) {
          return
        }
        const process = policy.business_process[processIndex]
        if (res.data && res.data.result) {
          if (process.type === 5) {
            process.process_data = res.data.data
          } else {
            const responseData = res.data.data
            if (responseData && responseData.id) {
              if (!process.process_data) {
                process.process_data = BpmOsgpoModule.BusinessProcess.defaultProcessData()
              }
              process.process_data.id = responseData.id
              if (responseData.status) {
                process.process_data.status = responseData.status
              }
            } else {
              process.process_data = {
                status: 'FAILED',
                message: 'Некорректный ответ запуска процесса',
              }
            }
          }
        } else {
          process.process_data = {
            status: 'FAILED',
            message: res.data && res.data.message ? res.data.message : 'Ошибка запуска процесса',
          }
          if (process.type !== 5) {
            notify.error(res.data)
          }
        }
        BpmOsgpoModule.BusinessProcess.touch(process)
        if (process.type === 5 && isProcessRejected(process.process_data)) {
          failedProcess(id)
          return
        }
        if (process.type === 5 && process.process_data?.id) {
          sharedApi.saveDraft()
        }
        syncMirrors(id)
      })
      .catch(err => {
        const processIndex = policy.business_process.findIndex(item => item.id === id)
        if (processIndex !== -1) {
          const process = policy.business_process[processIndex]
          process.process_data = {
            status: 'FAILED',
            message: err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'Ошибка запуска процесса',
          }
          BpmOsgpoModule.BusinessProcess.touch(process)
          if (process.type === 5) {
            failedProcess(id)
            return
          }
        }
        notify.error(err)
      })
  }

  function checkProcess (id) {
    if (processCheckInFlight.value[id]) {
      return
    }
    const index = policy.business_process.findIndex(item => item.id === id)
    const process = index !== -1 ? policy.business_process[index] : null
    const processId = getProcessCheckId(process)
    if (!processId) {
      return
    }
    processCheckInFlight.value[id] = true
    axios.post('/api/v2/process/bpm/check', { id: processId })
      .then(res => {
        const processIndex = policy.business_process.findIndex(item => item.id === id)
        if (processIndex === -1) {
          return
        }
        if (!res.data || !res.data.result) {
          throw new Error(res.data && res.data.message ? res.data.message : 'Ошибка получения статуса')
        }
        policy.business_process[processIndex].pollErrors = 0
        policy.business_process[processIndex].process_data = res.data.data
        BpmOsgpoModule.BusinessProcess.touch(policy.business_process[processIndex])
        if (policy.business_process[processIndex].type === 5 && res.data.data?.send_payment) {
          isSendPayment.value = true
          globalLoader.value = false
          stopInterval()
        }
        const processData = policy.business_process[processIndex].process_data
        if (processData && processData.status === 'COMPLETED') {
          completeProcess(id)
        }
        syncMirrors(id)
      })
      .catch(err => {
        const processIndex = policy.business_process.findIndex(item => item.id === id)
        if (processIndex === -1) {
          return
        }
        const failedProcessItem = policy.business_process[processIndex]
        const status = err.response?.status
        const isImmediateFailure = status === 404 || status === 422

        if (!isImmediateFailure) {
          failedProcessItem.pollErrors = (failedProcessItem.pollErrors ?? 0) + 1
          if (failedProcessItem.pollErrors < 3) {
            return
          }
        }

        failedProcessItem.process_data = {
          status: 'FAILED',
          message: err.response?.data?.message ?? err.message ?? 'Ошибка получения статуса',
        }
        BpmOsgpoModule.BusinessProcess.touch(failedProcessItem)
        syncMirrors(id)
        if (failedProcessItem.type === 5) {
          failedProcess(id)
        }
        notify.error(err)
      })
      .finally(() => {
        delete processCheckInFlight.value[id]
      })
  }

  function completeProcess (id) {
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index === -1) {
      return
    }
    const process = policy.business_process[index]
    policy.business_process[index].status = 3
    BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
    switch (process.type) {
      case 1: {
        let insurer = null
        if (Array.isArray(process?.process_data?.data) && process.process_data.data[0]) {
          insurer = structuredClone(process.process_data.data[0])
        }
        if (!insurer) {
          return
        }
        sharedApi.ensureClientVerifyState(insurer)
        policy.insurer = insurer
        const sameInsured = policy.insureds?.find(item => sharedApi.isSameClient(item, insurer))
        if (sameInsured) {
          policy.isDrive = true
          policy.insurer.driver = sameInsured.driver
          policy.insurer.licence = sameInsured.licence ?? policy.insurer.licence
          policy.insurer.client = sameInsured.client
        }
        sharedApi.verifyClient(policy.insurer)
        break
      }
      case 2: {
        let insured = null
        if (Array.isArray(process?.process_data?.data) && process.process_data.data[0]) {
          insured = structuredClone(process.process_data.data[0])
        }
        if (!insured) {
          return
        }
        if (policy.insurer && sharedApi.isSameClient(policy.insurer, insured)) {
          policy.isDrive = true
          policy.insurer.driver = insured.driver
          policy.insurer.client = insured.client
          policy.insurer.licence = insured.licence ?? policy.insurer.licence
        } else if (!policy.insureds.some(item => sharedApi.isSameClient(item, insured))) {
          sharedApi.ensureClientVerifyState(insured)
          policy.insureds.push(insured)
          sharedApi.verifyClient(policy.insureds[policy.insureds.length - 1])
        }
        break
      }
      case 3: {
        if (!Array.isArray(process?.process_data?.data) || !process.process_data.data[0]) {
          return
        }
        const vehicle = structuredClone(process.process_data.data[0])
        if (!policy.vehicle.some(item => sharedApi.isSameVehicle(item, vehicle))) {
          sharedApi.ensureVehicleVerifyState(vehicle)
          policy.vehicle.push(vehicle)
          sharedApi.verifyVehicle(policy.vehicle[policy.vehicle.length - 1])
        }
        break
      }
      case 5: {
        removeProcess(id)
        stopInterval()
        setTimeout(() => {
          changeDisabled.value = false
          globalLoader.value = false
          notify.success({ message: 'Полис успешно оформлен' })
          sharedApi.resetWizard()
          router.push({ path: '/bpm-ogpo' })
        }, 1000)
        break
      }
    }
  }

  /**
   * Зеркало не опрашивается и не отправляет действия: работа идёт через исходный процесс,
   * а зеркала получают его данные через syncMirrors.
   */
  function resolveSourceProcessId (id) {
    const process = policy.business_process.find(item => item.id === id)
    if (process?.mirror_of && policy.business_process.some(item => item.id === process.mirror_of)) {
      return process.mirror_of
    }
    return id
  }

  function updateProcess (data, id) {
    const sourceId = resolveSourceProcessId(id)
    const index = policy.business_process.findIndex(item => item.id === sourceId)
    if (index === -1) {
      return
    }
    policy.business_process[index].process_data = data
    BpmOsgpoModule.BusinessProcess.touch(policy.business_process[index])
    syncMirrors(sourceId)
  }

  function removeProcess (id) {
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index !== -1) {
      policy.business_process.splice(index, 1)
    }
  }

  function failedProcess (id) {
    const index = policy.business_process.findIndex(item => item.id === id)
    if (index === -1) {
      return
    }
    if (policy.business_process[index].type === 5) {
      changeDisabled.value = false
      globalLoader.value = false
      notify.error({ message: policy.business_process[index]?.process_data?.message || 'Ошибка оформления полиса' })
      removeProcess(id)
    }
  }

  function openBpmModal (id, closable = true) {
    isBpmModalOpened.value = true
    bpmModalClosable.value = closable
    processModalState.id = resolveSourceProcessId(id)
    const options = closable ? {} : { backdrop: 'static', keyboard: false }
    setTimeout(() => { new bootstrap.Modal('#processBpmModal', options).show() }, 10)
  }

  function closeBpmModal () {
    isBpmModalOpened.value = false
  }

  const activeBusinessProcessesByType = computed(() => {
    const result = { 1: [], 2: [], 3: [] }
    const processes = policy?.business_process
    if (!Array.isArray(processes)) {
      return result
    }
    for (const item of processes) {
      if (result[item.type] && BpmOsgpoModule.BusinessProcess.isActive(item)) {
        result[item.type].push(item)
      }
    }
    return result
  })

  return {
    activeBusinessProcessesByType,
    startInterval,
    stopInterval,
    addProcess,
    addMirrorProcess,
    changeProcessStatus,
    getProcessNomerVu,
    findInsurerProcessForInsured,
    shouldRunSeparateInsuredBpForInsurer,
    prepareSeparateInsuredBp,
    getProcessIin,
    processMatchesClient,
    processMatchesVehicle,
    canPollProcess,
    isProcessRejected,
    isInsurerMatchesInsuredProcess,
    findStartedProcessWithSameIin,
    syncMirrors,
    deleteProcess,
    removeBusinessProcessByEntity,
    removeUnfinishedInsuredBusinessProcessByInsurer,
    restartProcess,
    handleBusinessProcess,
    getProcessUrl,
    getProcessCheckId,
    startProcess,
    checkProcess,
    completeProcess,
    resolveSourceProcessId,
    updateProcess,
    removeProcess,
    failedProcess,
    openBpmModal,
    closeBpmModal,
  }
}
